import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { toSlug } from '../../../shared/utils/formatters';
import ServiceItem from './ServiceItem';

const DEFAULT_IMG = 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=150&q=80';

const smShimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

// Wrapper carries the vendor-class overrides (targets tp-service-* class names
// that the scroll-pin scripts rely on) plus the sc-card / skeleton styling.
const ServiceArea = styled.div`
  overflow-x: hidden;

  .tp-service-pin {
    height: auto !important;
  }

  .tp-service-panel {
    height: auto !important;
    overflow: visible !important;
    padding: 60px 0 !important;
  }

  .tp-service-content {
    overflow: visible !important;
    height: auto !important;
  }

  .tp-service-thumb {
    overflow: visible !important;
  }

  .tp-service-thumb video {
    max-height: 520px !important;
    height: auto !important;
    width: 100%;
    object-fit: cover;
    border-radius: 16px;
  }

  .tp-service-content p {
    margin-bottom: 20px;
  }

  .subcategories {
    margin-top: 24px !important;
  }

  @media (max-width: 1199px) {
    .tp-service-panel {
      padding: 40px 0 !important;
    }
    .tp-service-thumb video {
      max-height: 380px !important;
    }
  }

  @media (max-width: 767px) {
    &.pt-120 {
      padding-top: 50px;
    }
    /* container-fluid + gx-0 removes all gutters → content hits the screen
       edge on phones. Restore comfortable side padding. */
    .tp-service-panel {
      padding: 28px 16px !important;
    }
    .tp-service-title-box {
      padding: 0 16px;
    }
    .tp-service-number {
      margin-bottom: 4px;
    }
    .tp-service-thumb {
      text-align: center !important;
      margin-top: 20px;
    }
    .tp-service-thumb video {
      max-height: 260px !important;
      max-width: 100%;
    }
    .tp-service-number span {
      font-size: 22px;
    }
    /* Popular-services sub-cards: 2-up instead of stacked full-width. */
    .subcategories .col-md-4 {
      flex: 0 0 auto;
      width: 50%;
    }
  }
`;

const SubcategoryHeading = styled.h5`
  font-size: 20px;
  font-weight: 800;
  margin-bottom: 20px;
  color: #0f172a;
`;

// Loading skeleton — uses sm- prefix to avoid vendor script conflicts
const SmSkel = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: ${smShimmer} 1.4s infinite;
  border-radius: 8px;
`;

const SmLoadingPanel = styled.div`
  padding: 60px 0;
  border-bottom: 1px solid #f1f5f9;
`;

const ServicesMiddle = ({ categories = [], loading = false }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window !== 'undefined' && window.ScrollTrigger) {
      const timer = setTimeout(() => {
        window.ScrollTrigger.refresh();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [categories]);

  // Only first 3 panels — we have 01.mp4, 02.mp4, 03.mp4
  const panels = categories.slice(0, 3);

  return (
    <ServiceArea className="tp-service-area pt-120">
      <div className="container-fluid p-0">
        <div className="row gx-0">
          <div className="col-12">
            <div className="tp-service-title-box">
              <span className="tp-section-subtitle pre">Our services</span>
            </div>
          </div>
        </div>

        {/* Loading skeleton — deliberately avoids tp-service-panel / tp-service-item class
            names so vendor scroll-pin scripts cannot target and move these nodes before
            React has a chance to remove them. */}
        {loading && (
          <div>
            {Array.from({ length: 3 }).map((_, i) => (
              <SmLoadingPanel key={i} className="sm-loading-panel">
                <div className="row">
                  <div className="col-xxl-3 col-xl-2 col-lg-1 col-md-1">
                    <div style={{ padding: '0 20px' }}>
                      <SmSkel className="sm-skel" style={{ height: 28, width: 48 }} />
                    </div>
                  </div>
                  <div className="col-xxl-5 col-xl-6 col-lg-7 col-md-7" style={{ padding: '0 20px' }}>
                    <SmSkel className="sm-skel" style={{ height: 32, width: '55%', marginBottom: 20 }} />
                    <SmSkel className="sm-skel" style={{ height: 14, marginBottom: 8 }} />
                    <SmSkel className="sm-skel" style={{ height: 14, width: '80%', marginBottom: 8 }} />
                    <SmSkel className="sm-skel" style={{ height: 14, width: '50%', marginBottom: 32 }} />
                    <div className="row">
                      {[0, 1, 2].map(j => (
                        <div key={j} className="col-md-4 mb-3">
                          <div style={{ border: '1px solid #f1f5f9', borderRadius: 20, padding: '14px 14px 18px', textAlign: 'center' }}>
                            <SmSkel className="sm-skel" style={{ width: '100%', height: 190, borderRadius: 16, marginBottom: 16 }} />
                            <SmSkel className="sm-skel" style={{ height: 16, width: '65%', margin: '0 auto' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-4" style={{ padding: '0 20px' }}>
                    <SmSkel className="sm-skel" style={{ height: 340, borderRadius: 16 }} />
                  </div>
                </div>
              </SmLoadingPanel>
            ))}
          </div>
        )}

        {/* Real panels — only rendered once data is ready, so vendor scripts
            initialise on stable DOM nodes that React will never need to remove. */}
        <div className="tp-service-pin">
          {!loading && panels.map((cat, i) => {
            const catName = cat.categoryName ?? cat.name ?? '';
            const catSlug = toSlug(catName);
            const num = String(i + 1).padStart(2, '0');
            const desc = cat.subtitle ?? cat.description
              ?? `Professional ${catName} services at your doorstep.`;
            const subs = (cat.subCategories ?? cat.subcategories ?? []).slice(0, 3);

            return (
              <div key={cat.categoryID ?? cat.id ?? i} className="tp-service-item tp-service-panel">
                <div className="row">
                  <div className="col-xxl-3 col-xl-2 col-lg-1 col-md-1">
                    <div className="tp-service-number">
                      <span>{num}.</span>
                    </div>
                  </div>

                  <div className="col-xxl-5 col-xl-6 col-lg-7 col-md-7">
                    <div className="tp-service-content">
                      <h4 className="tp-section-title">
                        <a className="tp_text_invert" href={`/${catSlug}`}>
                          {catName}
                        </a>
                      </h4>
                      <p>{desc}</p>

                      {subs.length > 0 && (
                        <div className="subcategories">
                          <SubcategoryHeading className="subcategory-heading">Popular Services:</SubcategoryHeading>
                          <div className="row">
                            {subs.map(sub => (
                              <div key={sub.subCategoryID} className="col-md-4 mb-3">
                                <ServiceItem
                                  image={sub.imageURL || sub.imagePath || DEFAULT_IMG}
                                  title={sub.subCategoryName}
                                  onClick={() => navigate(`/${catSlug}/${sub.subCategoryID}`)}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="tp-service-btn mt-1">
                        <a href={`/${catSlug}`} className="tp-btn-black btn-red-bg">
                          <span className="tp-btn-black-filter-blur">
                            <svg width={0} height={0}>
                              <defs>
                                <filter id={`buttonFilter${i + 2}`}>
                                  <feGaussianBlur in="SourceGraphic" stdDeviation={5} result="blur" />
                                  <feColorMatrix in="blur" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 19 -9" />
                                  <feComposite in="SourceGraphic" in2={`buttonFilter${i + 2}`} operator="atop" />
                                  <feBlend in="SourceGraphic" in2={`buttonFilter${i + 2}`} />
                                </filter>
                              </defs>
                            </svg>
                          </span>
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-4">
                    <div className="tp-service-thumb text-end">
                      <video
                        className="tp_fade_anim"
                        data-fade-from="right"
                        data-delay=".2"
                        src={`assets/img/home-01/video/${num}.mp4`}
                        autoPlay
                        loop
                        muted
                        playsInline
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ServiceArea>
  );
};

export default ServicesMiddle;
